import { BrowserRouter as Router, Route } from "react-router-dom";
import { useDispatch } from "react-redux";
import { refreshToken } from "./redux/actions/authAction";
import { useEffect, memo, useState } from "react";
import "./App.css";
import AuthLayout from "./component/Layouts/AuthLayout";
import AdminAuthLayout from "./component/Layouts/AdminAuthLayout";
import DashboardLayout from "./component/Layouts/DashboardLayout";
import AdminLayout from "./component/Layouts/AdminLayout";

import Notify from "./component/Common/Notify";
import Home from "./component/Home";
import About from "./component/About";
import HowItWork from "./component/HowItWork";
import Blog from "./component/Blog";
import ContactUs from "./component/ContactUs";
import TermsOfUse from "./component/TermsOfUse";
import PrivacyPolicy from "./component/PrivacyPolicy";
import ImageView from "./component/ImageView";

/* Full-screen branded splash shown while the initial auth check is in-flight */
const AuthLoadingScreen = () => (
	<div style={{
		position: 'fixed', inset: 0,
		background: '#0e3725',
		display: 'flex', alignItems: 'center', justifyContent: 'center',
		zIndex: 9999,
	}}>
		<div style={{ textAlign: 'center' }}>
			<svg
				width="52" height="52" viewBox="0 0 24 24" fill="none"
				style={{ animation: 'treasury-spin 0.9s linear infinite', display: 'block', margin: '0 auto' }}
			>
				<circle cx="12" cy="12" r="10" stroke="rgba(201,168,76,0.25)" strokeWidth="2.5"/>
				<path d="M12 2a10 10 0 0 1 10 10" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round"/>
			</svg>
			<style>{`@keyframes treasury-spin { to { transform: rotate(360deg); } }`}</style>
		</div>
	</div>
);

function App() {
	const dispatch = useDispatch();
	const [authReady, setAuthReady] = useState(false);

	useEffect(() => {
		// Hard safety timeout: always dismiss the splash after 6s regardless of network state.
		// This prevents infinite loading on slow/mobile connections.
		const safetyTimer = setTimeout(() => setAuthReady(true), 6000);

		const result = dispatch(refreshToken());
		// refreshToken returns a Promise (redux-thunk); handle both promise and non-promise
		if (result && typeof result.then === 'function') {
			result.finally(() => {
				clearTimeout(safetyTimer);
				setAuthReady(true);
			});
		} else {
			clearTimeout(safetyTimer);
			setAuthReady(true);
		}
	}, [dispatch]);

	if (!authReady) {
		return <AuthLoadingScreen />;
	}

	return (
		<Router>
			<div className="App"> 
				<Notify />
				<Route path="/" exact component={Home} />
				<Route component={AuthLayout} path="/login" exact/>
				<Route component={AuthLayout} path="/register" exact/>
				<Route component={AuthLayout} path="/forgotPassword" exact/>
				
				<Route path="/about" exact component={About} />
				<Route path="/howitwork" exact component={HowItWork} />
				<Route path="/blog" exact component={Blog} />
				<Route path="/contactus" exact component={ContactUs} />
				<Route path="/termsofuse" exact component={TermsOfUse} />
				<Route path="/privacypolicy" exact component={PrivacyPolicy} />

				<Route path="/invoice/:imageName"  component={ImageView} />

				<Route component={DashboardLayout} path="/dashboard/profile" exact/>
				<Route component={DashboardLayout} path="/dashboard/createproperty" exact/>
				<Route component={DashboardLayout} path="/dashboard/viewproperty/:id" exact/>
				<Route component={DashboardLayout} path="/dashboard/tradeproperty/:id" exact/>
				<Route component={DashboardLayout} path="/dashboard/transactions" exact/>
				<Route component={DashboardLayout} path="/dashboard/portfolio" exact/>
				<Route component={DashboardLayout} path="/dashboard/withdrawals" exact/>

				<Route component={AdminAuthLayout} path="/admin/login" exact/>

				<Route component={AdminLayout} path="/admin/dashboard" exact/>
				<Route component={AdminLayout} path="/admin/profile" exact/>
				<Route component={AdminLayout} path="/admin/createproperty" exact/>
				<Route component={AdminLayout} path="/admin/viewproperty/:id" exact/>
				<Route component={AdminLayout} path="/admin/tradeproperty/:id" exact/>
				<Route component={AdminLayout} path="/admin/transactions" exact/>
				<Route component={AdminLayout} path="/admin/blockchaindata" exact/>
				<Route component={AdminLayout} path="/admin/users" exact/>
				<Route component={AdminLayout} path="/admin/requestFund" exact/>
				<Route component={AdminLayout} path="/admin/properties" exact/>
				<Route component={AdminLayout} path="/admin/property/:id" exact/>
				<Route component={AdminLayout} path="/admin/user/:userId" exact/>
				<Route component={AdminLayout} path="/admin/integrations" exact/>
				<Route component={AdminLayout} path="/admin/integrations/nafath" exact/>
				<Route component={AdminLayout} path="/admin/integrations/rer" exact/>
				<Route component={AdminLayout} path="/admin/integrations/virtual-iban" exact/>
				<Route component={AdminLayout} path="/admin/fees" exact/>
				<Route component={AdminLayout} path="/admin/finance" exact/>
				<Route component={AdminLayout} path="/admin/withdrawals" exact/>
				<Route component={AdminLayout} path="/admin/evaluations/:propertyId" exact/>
				<Route component={AdminLayout} path="/admin/expenses/:propertyId" exact/>
				<Route component={AdminLayout} path="/admin/report/:propertyId" exact/>
				<Route component={AdminLayout} path="/admin/news" exact/>
				<Route component={AdminLayout} path="/admin/alinma" exact/>
			</div>
		</Router>
	);
}

export default memo(App);
